import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function App() {
  const [scrollY, setScrollY] = useState(0);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 text-neutral-900 antialiased selection:bg-indigo-200 selection:text-indigo-900 overflow-x-hidden">
      {/* Animated gradient orbs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-gradient-to-br from-indigo-400/20 via-purple-400/20 to-pink-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-gradient-to-tr from-violet-400/15 via-fuchsia-400/15 to-indigo-400/15 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      {/* Navigation - Enhanced */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrollY > 20 
          ? 'bg-white/70 backdrop-blur-2xl border-b border-neutral-200/60 shadow-lg shadow-indigo-500/5' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="text-[1.75rem] font-bold tracking-[-0.02em] bg-gradient-to-r from-neutral-900 via-indigo-900 to-violet-900 bg-clip-text text-transparent hover:scale-105 transition-transform cursor-pointer">
            RolfKorp
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={toggleLanguage}
              className="text-sm font-medium text-neutral-600 hover:text-indigo-600 transition-all duration-300 uppercase tracking-wide px-3 py-2 rounded-lg hover:bg-indigo-50"
            >
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
            <a 
              href="mailto:rolfkorp@gmail.com"
              className="text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 transition-all duration-300 px-5 py-2.5 rounded-lg shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5"
            >
              {t('nav.contact')}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero - Enhanced with Gradient */}
      <section className="min-h-screen flex items-center justify-center px-6 py-32 relative">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Decorative elements */}
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-40 h-40 bg-gradient-to-br from-indigo-400/20 to-violet-400/20 rounded-full blur-3xl" />
          
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200/50 rounded-full text-sm text-indigo-700 font-medium mb-8 shadow-sm">
            <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
            Software Products
          </div>
          
          <h1 className="text-[3.5rem] sm:text-7xl lg:text-8xl font-bold tracking-[-0.03em] leading-[1.1] mb-8 animate-fade-in-up">
            <span className="bg-gradient-to-r from-neutral-900 via-indigo-900 to-violet-900 bg-clip-text text-transparent">
              {t('hero.title').split('\\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i === 0 && <br className="hidden sm:block" />}
                </span>
              ))}
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl lg:text-2xl text-neutral-600 max-w-2xl mx-auto mb-12 leading-relaxed">
            {t('hero.subtitle')}
          </p>
          
          <a
            href="mailto:rolfkorp@gmail.com"
            className="group inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white text-base font-semibold rounded-xl hover:shadow-2xl hover:shadow-indigo-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-1"
          >
            {t('hero.cta')}
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>

      {/* What We Do - Vibrant Cards */}
      <section className="py-24 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200/50 rounded-full text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-4">
              Capacidades
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-[-0.02em]">
              {t('whatWeDo.title')}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Product Development */}
            <div className="group relative p-8 rounded-2xl bg-white border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-blue-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-indigo-500 to-blue-500 opacity-0 group-hover:opacity-10 rounded-full blur-3xl transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3 tracking-[-0.01em] group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-indigo-600 group-hover:to-violet-600 transition-all duration-300">
                  {t('whatWeDo.product.title')}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-[0.9375rem]">
                  {t('whatWeDo.product.description')}
                </p>
              </div>
            </div>

            {/* Custom Solutions */}
            <div className="group relative p-8 rounded-2xl bg-white border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500 to-purple-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-violet-500 to-purple-500 opacity-0 group-hover:opacity-10 rounded-full blur-3xl transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3 tracking-[-0.01em] group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-indigo-600 group-hover:to-violet-600 transition-all duration-300">
                  {t('whatWeDo.custom.title')}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-[0.9375rem]">
                  {t('whatWeDo.custom.description')}
                </p>
              </div>
            </div>

            {/* Integrations */}
            <div className="group relative p-8 rounded-2xl bg-white border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-purple-500 to-pink-500 opacity-0 group-hover:opacity-10 rounded-full blur-3xl transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3 tracking-[-0.01em] group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-indigo-600 group-hover:to-violet-600 transition-all duration-300">
                  {t('whatWeDo.integrations.title')}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-[0.9375rem]">
                  {t('whatWeDo.integrations.description')}
                </p>
              </div>
            </div>

            {/* Evolution */}
            <div className="group relative p-8 rounded-2xl bg-white border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500 to-pink-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-fuchsia-500 to-pink-500 opacity-0 group-hover:opacity-10 rounded-full blur-3xl transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3 tracking-[-0.01em] group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-indigo-600 group-hover:to-violet-600 transition-all duration-300">
                  {t('whatWeDo.evolution.title')}
                </h3>
                <p className="text-neutral-600 leading-relaxed text-[0.9375rem]">
                  {t('whatWeDo.evolution.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach - Our Philosophy */}
      <section className="py-24 px-6 bg-gradient-to-br from-indigo-50/50 via-violet-50/30 to-purple-50/50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-indigo-300/20 to-violet-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-300/20 to-pink-300/20 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="mb-16 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200/50 rounded-full text-xs font-semibold text-violet-700 uppercase tracking-wider mb-4">
              {t('approach.badge')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-[-0.02em]">
              {t('approach.title')}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="group relative text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-blue-600 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">{t('approach.quality.title')}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                {t('approach.quality.description')}
              </p>
            </div>
            
            <div className="group relative text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-600 to-purple-600 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">{t('approach.communication.title')}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                {t('approach.communication.description')}
              </p>
            </div>
            
            <div className="group relative text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-neutral-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-600 to-pink-600 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">{t('approach.speed.title')}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                {t('approach.speed.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Products - Card Design Excellence */}
      <section className="py-24 px-6 relative overflow-hidden bg-gradient-to-b from-neutral-50 to-white">
        {/* Gradient Decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-violet-200/30 via-purple-200/20 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="mb-16">
            <p className="text-xs font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent uppercase tracking-wider mb-3">Portfolio</p>
            <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-neutral-900 via-indigo-900 to-violet-900 bg-clip-text text-transparent tracking-[-0.02em] mb-3">
              {t('products.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl">
              {t('products.subtitle')}
            </p>
          </div>
          
          <div className="grid gap-6">
            {/* BoviTrack - Beta */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-neutral-200/60 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500">
              {/* Gradient Background on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 via-sky-50/0 to-cyan-50/0 group-hover:from-blue-50/80 group-hover:via-sky-50/40 group-hover:to-cyan-50/80 transition-all duration-500"></div>
              
              <div className="relative p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent tracking-[-0.01em] group-hover:scale-105 transition-transform duration-300">
                        {t('products.bovitrack.name') || 'BoviTrack'}
                      </h3>
                      <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs font-semibold rounded-full shadow-sm">
                        {t('products.bovitrack.status') || 'Beta'}
                      </span>
                    </div>
                    <p className="text-neutral-900 text-base font-medium mb-2">
                      {t('products.bovitrack.description') || 'Plataforma integral de gestión para granjas ganaderas bovinas.'}
                    </p>
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {t('products.bovitrack.details') || 'Control de rodeos, sanidad animal, reproducción y trazabilidad completa.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-blue-500 via-sky-500 to-cyan-500"></div>
            </div>

            {/* Avikorp - In Development */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-neutral-200/60 hover:border-amber-200 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500">
              {/* Gradient Background on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50/0 via-orange-50/0 to-yellow-50/0 group-hover:from-amber-50/80 group-hover:via-orange-50/40 group-hover:to-yellow-50/80 transition-all duration-500"></div>
              
              <div className="relative p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent tracking-[-0.01em] group-hover:scale-105 transition-transform duration-300">
                        {t('products.avikorp.name') || 'Avikorp'}
                      </h3>
                      <span className="px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 text-xs font-semibold rounded-full shadow-sm">
                        {t('products.avikorp.status') || 'En desarrollo'}
                      </span>
                    </div>
                    <p className="text-neutral-900 text-base font-medium mb-2">
                      {t('products.avikorp.description') || 'Sistema completo de administración de granjas avícolas.'}
                    </p>
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {t('products.avikorp.details') || 'Gestión de galpones, control de lotes, alimentación y producción de huevos.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500"></div>
            </div>

            {/* BuildOS - In Development */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-neutral-200/60 hover:border-violet-200 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500">
              {/* Gradient Background on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-violet-50/0 via-purple-50/0 to-fuchsia-50/0 group-hover:from-violet-50/80 group-hover:via-purple-50/40 group-hover:to-fuchsia-50/80 transition-all duration-500"></div>
              
              <div className="relative p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent tracking-[-0.01em] group-hover:scale-105 transition-transform duration-300">
                        {t('products.buildos.name') || 'BuildOS'}
                      </h3>
                      <span className="px-3 py-1 bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 text-xs font-semibold rounded-full shadow-sm">
                        {t('products.buildos.status') || 'En desarrollo'}
                      </span>
                    </div>
                    <p className="text-neutral-900 text-base font-medium mb-2">
                      {t('products.buildos.description') || 'Plataforma de gestión integral para empresas constructoras.'}
                    </p>
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {t('products.buildos.details') || 'Administración de obras, presupuestos, proveedores y control de proyectos.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy - Centered Message */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Gradient Orb */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-indigo-200/40 via-violet-200/30 to-purple-200/40 rounded-full blur-3xl animate-pulse-slow"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-2xl sm:text-3xl lg:text-4xl font-semibold bg-gradient-to-r from-neutral-900 via-indigo-900 to-violet-900 bg-clip-text text-transparent mb-6 leading-tight tracking-[-0.02em]">
            {t('philosophy.main')}
          </p>
          <p className="text-lg sm:text-xl text-neutral-600">
            {t('philosophy.sub')}
          </p>
        </div>
      </section>

      {/* Contact - Clean & Direct */}
      <section className="py-24 px-6 bg-gradient-to-br from-neutral-900 via-neutral-900 to-indigo-950 relative overflow-hidden">
        {/* Gradient Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-indigo-600/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-violet-600/20 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent uppercase tracking-wider mb-4">
                {t('contact.title')}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-indigo-100 to-violet-100 bg-clip-text text-transparent mb-8 leading-tight tracking-[-0.02em]">
                {t('contact.subtitle')}
              </h2>
              
              <a
                href="mailto:rolfkorp@gmail.com"
                className="group inline-flex items-center gap-3 text-xl font-semibold text-white hover:text-transparent hover:bg-gradient-to-r hover:from-indigo-400 hover:to-violet-400 hover:bg-clip-text transition-all duration-300"
              >
                rolfkorp@gmail.com
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
            
            <div className="space-y-6">
              <div className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center flex-shrink-0 mt-1 shadow-lg shadow-indigo-500/30 group-hover:shadow-indigo-500/50 group-hover:scale-110 transition-all duration-300">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">{t('contact.features.fast.title')}</div>
                  <div className="text-sm text-neutral-400">{t('contact.features.fast.description')}</div>
                </div>
              </div>
              
              <div className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-violet-700 flex items-center justify-center flex-shrink-0 mt-1 shadow-lg shadow-violet-500/30 group-hover:shadow-violet-500/50 group-hover:scale-110 transition-all duration-300">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">{t('contact.features.commitment.title')}</div>
                  <div className="text-sm text-neutral-400">{t('contact.features.commitment.description')}</div>
                </div>
              </div>
              
              <div className="group flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0 mt-1 shadow-lg shadow-purple-500/30 group-hover:shadow-purple-500/50 group-hover:scale-110 transition-all duration-300">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">{t('contact.features.custom.title')}</div>
                  <div className="text-sm text-neutral-400">{t('contact.features.custom.description')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gradient-to-br from-neutral-950 via-neutral-900 to-indigo-950 border-t border-neutral-800/50 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-gradient-to-b from-indigo-600/10 to-transparent blur-2xl"></div>
        <div className="max-w-6xl mx-auto relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col items-center md:items-start gap-2">
              <div className="text-xl font-bold bg-gradient-to-r from-white via-indigo-100 to-violet-100 bg-clip-text text-transparent">RolfKorp</div>
              <div className="text-sm text-neutral-500">{t('footer.copyright')}</div>
            </div>
            <div className="text-sm text-neutral-400">{t('footer.tagline')}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
